// ABOUTME: Pure helpers for client-side pennant team statistics
// ABOUTME: Keeps HUD stats deterministic for loaded, simulated, and local pennants

// Extract the authoritative pennant total from a GET /stats payload
// ({ pennant_count }). Returns null for missing/invalid data so callers can
// fall back to the local optimistic count (e.g. the DB-less demo has no API).
export function parseStatsCount(payload) {
    const raw = payload?.pennant_count;
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return null;
    return Math.floor(raw);
}

// Pick the number the counter should show. The real DB total wins when known,
// but never drops the display below the local count — a just-planted pennant
// (local ahead of the last /stats poll) must not make the counter tick backwards.
export function resolveDisplayedCount(statsCount, localFallback) {
    const local = Number.isFinite(localFallback) ? localFallback : 0;
    if (statsCount === null || statsCount === undefined) return local;
    return Math.max(statsCount, local);
}

export function createTeamCounts(teams) {
    return new Map(teams.map((team) => [team.code, 0]));
}

export function incrementTeamCount(counts, teamCode, amount = 1) {
    if (!teamCode) return counts;
    counts.set(teamCode, (counts.get(teamCode) ?? 0) + amount);
    return counts;
}

export function summarizeTeamCounts(counts, teams, { limit = Infinity } = {}) {
    const teamByCode = new Map(teams.map((team) => [team.code, team]));
    const rows = [];
    let total = 0;

    for (const [code, count] of counts) {
        if (count <= 0) continue;
        total += count;
        const team = teamByCode.get(code);
        rows.push({
            code,
            count,
            nameDe: team?.nameDe ?? code,
            nameEn: team?.nameEn ?? code,
            emblem: team?.emblem ?? null,
            primary: team?.primary ?? '#FFFFFF',
        });
    }

    rows.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.nameDe.localeCompare(b.nameDe, 'de');
    });

    const leader = rows[0] ?? null;
    const leaderShare = leader && total > 0 ? leader.count / total : 0;

    return {
        total,
        representedTeamCount: rows.length,
        leader,
        leaderShare,
        rows,
        topRows: rows.slice(0, limit),
    };
}
